import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { useNavigate } from "react-router-dom";
import { CircularProgress, Box, Typography, Link } from "@mui/material";

import DefaultButton from "../../../components/simple/DefaultButton";
import DefaultBadge from "../../../components/simple/DefaultBadge";
import { Container } from "./style.js";

import RestartAltIcon from "@mui/icons-material/RestartAlt";

dayjs.extend(duration);

const SimMobileCards = ({
  simulations,
  theme,
  page,
  onPageChange,
  sortOrder,
  setDeleteConfirmModal,
  paginationData,
  isLoading,
  deletedId,
  updateFilters,
}) => {
  const navigate = useNavigate();
  const [accumulatedRows, setAccumulatedRows] = useState([]);
  const observer = useRef(null);

  useEffect(() => {
    if (deletedId) {
      setAccumulatedRows((prevRows) =>
        prevRows.filter((row) => row.id !== deletedId),
      );
    }
  }, [deletedId]);

  // 1. Process the incoming batch of simulations
  const newRows = useMemo(() => {
    if (!simulations) return [];

    const rows = Object.values(simulations).map((item) => {
      const start = dayjs(item.started_at);
      const end = dayjs(item.ended_at);
      const diff = dayjs.duration(end.diff(start));

      // Keep raw numeric score for sorting, formatted string for display
      const rawScore = item.evaluation?.total_score?.value
        ? parseFloat(item.evaluation.total_score.value)
        : 0;
      const scoreDisplay = item.evaluation?.total_score?.value
        ? (rawScore / 10).toFixed(1) + "/10"
        : "N/A";

      return {
        id: item.id,
        scenarioName: item.scenario.name,
        description:
          item.scenario?.short_description || "Unknown Simulation Challenge",
        score: scoreDisplay,
        rawScore: rawScore, // Added for sorting
        dateCompleted: end.format("MMM DD, YYYY"),
        timeCompleted: end.format("HH:mm A"),
        completedAtUnix: end.valueOf(), // Added for sorting
        durationMs: end.diff(start),
        durationDisplay: `${diff.minutes()}m ${diff.seconds()}s`,
      };
    });

    // Apply Sorting
    return rows.sort((a, b) => {
      switch (sortOrder) {
        case "score":
          return b.rawScore - a.rawScore; // High to Low
        case "completed_asc":
          return a.completedAtUnix - b.completedAtUnix;
        case "completed_desc":
          return b.completedAtUnix - a.completedAtUnix;
        case "sim_duration":
          return b.durationMs - a.durationMs; // Longest first
        default:
          return 0; // Keep original order
      }
    });
  }, [simulations, sortOrder]);

  // 2. Accumulate rows (so standard pagination acts like infinite scroll)
  useEffect(() => {
    if (page === 0) {
      // If we are on page 0 (initial load or filters reset), reset the list
      setAccumulatedRows(newRows);
    } else {
      // Otherwise, append new rows. We deduplicate by ID just in case React 18
      // strict mode double-invokes the effect.
      setAccumulatedRows((prev) => {
        const uniqueNewRows = newRows.filter(
          (newRow) => !prev.some((prevRow) => prevRow.id === newRow.id),
        );
        return [...prev, ...uniqueNewRows];
      });
    }
  }, [newRows, page]);

  // 3. Set up the Intersection Observer on the last element
  const lastElementRef = useCallback(
    (node) => {
      if (isLoading) return; // Don't trigger if already fetching
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && paginationData?.has_next) {
          onPageChange(page + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, paginationData?.has_next, page, onPageChange],
  );

  // 4. Smooth scroll to top handler
  const scrollToTop = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Container id="sim-mobile-card-container">
      {accumulatedRows.length > 0 ? (
        <ul>
          {accumulatedRows.map((sim, i) => {
            const isLastElement = accumulatedRows.length === i + 1;

            return (
              <li
                key={sim.id}
                ref={isLastElement ? lastElementRef : null}
                onClick={() => navigate(`/dashboard/feedback/${sim.id}`)}
              >
                <h3>{sim.scenarioName}</h3>
                <p>{sim.description}</p>
                <ol>
                  <li>
                    <strong>Score</strong>
                    {sim.score !== "N/A" ? (
                      <DefaultBadge
                        className="hover-badge"
                        variant="pillTransparent"
                        borderColor={theme.palette.border.brand}
                        size="small"
                      >
                        {sim.score}
                      </DefaultBadge>
                    ) : (
                      <DefaultBadge size="small">N/A</DefaultBadge>
                    )}
                  </li>
                  <li>
                    <strong>Duration</strong> {sim.durationDisplay}
                  </li>
                  <li>
                    <strong>Date</strong> {sim.dateCompleted}
                  </li>
                  <li>
                    <strong>Time</strong> {sim.timeCompleted}
                  </li>
                </ol>
                <DefaultButton
                  className="delete-sim-btn"
                  backgroundColor="transparent"
                  textColor={theme.palette.text.primary}
                  borderColor={theme.palette.border?.brand}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirmModal({ show: true, id: sim.id });
                  }}
                >
                  <svg
                    width="17"
                    height="19"
                    viewBox="0 0 17 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.6667 4.16683V3.50016C11.6667 2.56674 11.6667 2.10003 11.485 1.74351C11.3252 1.42991 11.0703 1.17494 10.7567 1.01515C10.4001 0.833496 9.93342 0.833496 9 0.833496H7.66667C6.73325 0.833496 6.26654 0.833496 5.91002 1.01515C5.59641 1.17494 5.34145 1.42991 5.18166 1.74351C5 2.10003 5 2.56674 5 3.50016V4.16683M6.66667 8.75016V12.9168M10 8.75016V12.9168M0.833336 4.16683H15.8333M14.1667 4.16683V13.5002C14.1667 14.9003 14.1667 15.6004 13.8942 16.1351C13.6545 16.6055 13.2721 16.988 12.8016 17.2277C12.2669 17.5002 11.5668 17.5002 10.1667 17.5002H6.5C5.09987 17.5002 4.39981 17.5002 3.86503 17.2277C3.39462 16.988 3.01217 16.6055 2.77249 16.1351C2.5 15.6004 2.5 14.9003 2.5 13.5002V4.16683"
                      stroke="#FF6467"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Delete Simulation
                </DefaultButton>
              </li>
            );
          })}
        </ul>
      ) : (
        !isLoading && (
          <div className="no-results">
            <strong>No results matching your search criteria</strong>
            <button
              onClick={() =>
                updateFilters({ date_range_start: "", date_range_end: "" })
              }
            >
              Reset Filters <RestartAltIcon />
            </button>
          </div>
        )
      )}

      {/* Loader UI */}
      {isLoading && page > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 3,
            gap: "15px",
          }}
        >
          <CircularProgress size={20} sx={{ color: "rgb(168, 126, 255)" }} />
          <Typography sx={{ fontSize: "14px" }}>Loading more...</Typography>
        </Box>
      )}

      {/* End of Results UI */}
      {!paginationData?.has_next && accumulatedRows.length > 0 && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography
            sx={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "14px", mb: 5 }}
          >
            No more simulations to show
          </Typography>
          <Link
            href="#"
            onClick={scrollToTop}
            sx={{
              color: "rgb(1, 236, 255)",
              textDecoration: "none",
              fontSize: "14px",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Back to top &uarr;
          </Link>
        </Box>
      )}
    </Container>
  );
};

export default SimMobileCards;
